import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Button,
  Modal,
  Form,
  Input,
  Select,
  message,
  Space,
  Tabs,
  Tooltip,
  Upload,
} from 'antd';
import AdaptiveTable from '../components/AdaptiveTable';
import FilterDrawer from '../components/FilterDrawer';
import {
  cartonApi,
  cartonBatchApi,
  supplierApi,
  customerApi,
  type CartonBatchDTO,
  type CartonDTO,
  type ReceiveCartonRequest,
} from '../api/kfc';
import CartonDetailDrawer from '../components/CartonDetailDrawer';
import CartonBatchFormModal from '../components/CartonBatchFormModal';
import CartonBatchDetailDrawer from '../components/CartonBatchDetailDrawer';
import { serverTablePagination } from '../utils/tablePagination';
import { highlightText } from '../utils/highlightText';

const STATE_LABELS: Record<string, string> = {
  preparing: '待收货',
  inKFYWarehouse: '抵达凯飞越仓库',
  inAgentWarehouse: '抵达代理仓库(出发地)',
  readyForShipping: '准备运输',
  onTheWay: '在途运输',
  arrivedDstPort: '抵达目的港',
  recevied: '已签收',
};

type TabKey = 'batches' | 'cartons';

const CartonList: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabKey>('batches');
  const [suppliers, setSuppliers] = useState<{ value: string; label: string }[]>([]);
  const [customers, setCustomers] = useState<{ value: string; label: string }[]>([]);
  const [addModalOpen, setAddModalOpen] = useState(false);

  // 批次页签
  const [batchList, setBatchList] = useState<CartonBatchDTO[]>([]);
  const [batchTotal, setBatchTotal] = useState(0);
  const [batchPage, setBatchPage] = useState(0);
  const [batchPageSize, setBatchPageSize] = useState(20);
  const [batchLoading, setBatchLoading] = useState(false);
  const [filterBatchIdInput, setFilterBatchIdInput] = useState('');
  const [appliedBatchIdKeyword, setAppliedBatchIdKeyword] = useState('');
  const [batchDrawerId, setBatchDrawerId] = useState<string | undefined>(undefined);

  // 单箱页签
  const [list, setList] = useState<CartonDTO[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [loading, setLoading] = useState(false);
  const [detailDrawerOpen, setDetailDrawerOpen] = useState(false);
  const [detailCartonId, setDetailCartonId] = useState<string | undefined>(undefined);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [receiveModalOpen, setReceiveModalOpen] = useState(false);
  const [receiveTargetIds, setReceiveTargetIds] = useState<string[]>([]);
  const [receiveSubmitting, setReceiveSubmitting] = useState(false);
  const [receiveForm] = Form.useForm();
  const [receivePictures, setReceivePictures] = useState<{ desc: string; base64: string }[]>([]);
  const [supplierModalOpen, setSupplierModalOpen] = useState(false);
  const [supplierTargetIds, setSupplierTargetIds] = useState<string[]>([]);
  const [supplierSubmitting, setSupplierSubmitting] = useState(false);
  const [supplierForm] = Form.useForm();
  const [filterCartonIdInput, setFilterCartonIdInput] = useState('');
  const [filterShipmentIdInput, setFilterShipmentIdInput] = useState('');
  const [appliedCartonIdKeyword, setAppliedCartonIdKeyword] = useState('');
  const [appliedShipmentIdKeyword, setAppliedShipmentIdKeyword] = useState('');

  const loadBatches = useCallback((pageIndex = batchPage) => {
    setBatchLoading(true);
    cartonBatchApi
      .list({
        page: pageIndex,
        size: batchPageSize,
        batchIdKeyword: appliedBatchIdKeyword || undefined,
      })
      .then((res) => {
        setBatchList(res.data?.content || []);
        setBatchTotal(res.data?.totalElements ?? 0);
      })
      .catch(() => {
        setBatchList([]);
        setBatchTotal(0);
        message.warning('无法加载批次列表');
      })
      .finally(() => setBatchLoading(false));
  }, [batchPage, batchPageSize, appliedBatchIdKeyword]);

  useEffect(() => {
    loadBatches(batchPage);
  }, [batchPage, batchPageSize, appliedBatchIdKeyword, loadBatches]);

  const load = useCallback((pageIndex = page) => {
    setLoading(true);
    const params: Parameters<typeof cartonApi.list>[0] = {
      page: pageIndex,
      size: pageSize,
    };
    if (appliedCartonIdKeyword) params.cartonIdKeyword = appliedCartonIdKeyword;
    if (appliedShipmentIdKeyword) params.shipmentIdKeyword = appliedShipmentIdKeyword;
    cartonApi
      .list(params)
      .then((res) => {
        const body = res.data;
        if (Array.isArray(body)) {
          setList(body);
          setTotal(body.length);
        } else {
          setList(cartonApi.unwrapList(body));
          setTotal(body?.totalElements ?? 0);
        }
      })
      .catch(() => {
        setList([]);
        setTotal(0);
        message.warning('无法加载货物列表');
      })
      .finally(() => setLoading(false));
  }, [page, pageSize, appliedCartonIdKeyword, appliedShipmentIdKeyword]);

  useEffect(() => {
    if (activeTab === 'cartons') load(page);
  }, [activeTab, page, pageSize, appliedCartonIdKeyword, appliedShipmentIdKeyword, load]);

  const applyBatchFilter = () => {
    setAppliedBatchIdKeyword(filterBatchIdInput.trim());
    setBatchPage(0);
  };

  const resetBatchFilter = () => {
    setFilterBatchIdInput('');
    setAppliedBatchIdKeyword('');
    setBatchPage(0);
  };

  const applyFilters = () => {
    setAppliedCartonIdKeyword(filterCartonIdInput.trim());
    setAppliedShipmentIdKeyword(filterShipmentIdInput.trim());
    setPage(0);
    setSelectedRowKeys([]);
  };

  const resetFilters = () => {
    setFilterCartonIdInput('');
    setFilterShipmentIdInput('');
    setAppliedCartonIdKeyword('');
    setAppliedShipmentIdKeyword('');
    setPage(0);
    setSelectedRowKeys([]);
  };

  useEffect(() => {
    supplierApi.list().then((res) => {
      const data = (res.data || []).map((s) => ({ value: s.id!, label: `${s.supplierName || s.supplierId || s.id}` }));
      setSuppliers(data);
    }).catch(() => setSuppliers([]));
    customerApi.list().then((res) => {
      const data = (res.data || []).map((c) => ({ value: c.id!, label: `${c.name || c.customerCode || c.id}` }));
      setCustomers(data);
    }).catch(() => setCustomers([]));
  }, []);

  const supplierLabel = (id?: string) => suppliers.find((s) => s.value === id)?.label || id || '-';
  const customerLabel = (id?: string) => customers.find((c) => c.value === id)?.label || id || '-';

  const openDetail = (record: CartonDTO) => {
    if (!record.id) return;
    setDetailCartonId(record.id);
    setDetailDrawerOpen(true);
  };

  const closeDetail = () => {
    setDetailDrawerOpen(false);
    setDetailCartonId(undefined);
  };

  const onBatchCreated = () => {
    loadBatches(0);
    setBatchPage(0);
    if (activeTab === 'cartons') load();
  };

  const renderState = (state: string) => STATE_LABELS[state] || state;

  const openReceiveModal = (ids: string[]) => {
    const validIds = ids.filter(Boolean);
    if (validIds.length === 0) {
      message.warning('请先选择要收货的货物');
      return;
    }
    setReceiveTargetIds(validIds);
    receiveForm.resetFields();
    setReceivePictures([]);
    setReceiveModalOpen(true);
  };

  const openSupplierModal = (ids: string[]) => {
    const validIds = ids.filter(Boolean);
    if (validIds.length === 0) {
      message.warning('请先选择要修改供应商的货物');
      return;
    }
    setSupplierTargetIds(validIds);
    supplierForm.resetFields();
    setSupplierModalOpen(true);
  };

  const closeSupplierModal = () => {
    setSupplierModalOpen(false);
    setSupplierTargetIds([]);
    supplierForm.resetFields();
  };

  const submitBatchUpdateSupplier = () => {
    supplierForm.validateFields().then((values) => {
      setSupplierSubmitting(true);
      cartonApi
        .batchUpdateSupplier({
          cartonIds: supplierTargetIds,
          fromSupplierId: values.fromSupplierId,
        })
        .then(() => {
          message.success(`已更新 ${supplierTargetIds.length} 箱供应商`);
          closeSupplierModal();
          setSelectedRowKeys([]);
          load();
        })
        .catch((e) => message.error(e.response?.data?.message || '批量更新供应商失败'))
        .finally(() => setSupplierSubmitting(false));
    });
  };

  const closeReceiveModal = () => {
    setReceiveModalOpen(false);
    setReceiveTargetIds([]);
    setReceivePictures([]);
  };

  const addReceivePicture = () => {
    setReceivePictures((prev) => [...prev, { desc: '', base64: '' }]);
  };

  const removeReceivePicture = (index: number) => {
    setReceivePictures((prev) => prev.filter((_, i) => i !== index));
  };

  const setReceivePictureDesc = (index: number, desc: string) => {
    setReceivePictures((prev) => {
      const next = [...prev];
      if (!next[index]) return prev;
      next[index] = { ...next[index], desc };
      return next;
    });
  };

  const setReceivePictureBase64 = (index: number, base64: string) => {
    setReceivePictures((prev) => {
      const next = [...prev];
      if (!next[index]) return prev;
      next[index] = { ...next[index], base64 };
      return next;
    });
  };

  const submitReceive = () => {
    receiveForm.validateFields().then((values) => {
      const pictures: Record<string, string> = {};
      receivePictures.forEach((p) => {
        const desc = (p.desc || '照片').trim();
        if (p.base64) pictures[desc] = p.base64;
      });
      const body: ReceiveCartonRequest = {
        arrivedBy: values.arrivedBy,
        queryParamOfShippment: values.queryParamOfShippment?.trim() || undefined,
        pictures: Object.keys(pictures).length ? pictures : undefined,
      };
      setReceiveSubmitting(true);
      const run = () => {
        if (receiveTargetIds.length === 1) {
          return cartonApi.receive(receiveTargetIds[0], body).then((r) => [r.data]);
        }
        return cartonApi.receiveBatch({ cartonIds: receiveTargetIds, ...body }).then((r) => r.data || []);
      };
      run()
        .then(() => {
          message.success(`已收货 ${receiveTargetIds.length} 箱`);
          closeReceiveModal();
          setSelectedRowKeys([]);
          load();
        })
        .catch((e) => message.error(e.response?.data?.message || '收货失败'))
        .finally(() => setReceiveSubmitting(false));
    });
  };

  const batchTab = (
    <>
      <FilterDrawer variant="space" style={{ marginBottom: 16 }}>
        <Input
          placeholder="模糊搜索批次标识"
          allowClear
          style={{ width: 220 }}
          value={filterBatchIdInput}
          onChange={(e) => setFilterBatchIdInput(e.target.value)}
          onPressEnter={applyBatchFilter}
        />
        <Button type="primary" onClick={applyBatchFilter}>查询</Button>
        <Button onClick={resetBatchFilter}>重置</Button>
      </FilterDrawer>
      <AdaptiveTable<CartonBatchDTO>
        loading={batchLoading}
        dataSource={batchList}
        rowKey="id"
        scroll={{ x: 1000 }}
        onRow={(record) => ({
          onClick: () => record.id && setBatchDrawerId(record.id),
          style: { cursor: 'pointer' },
        })}
        columns={[
          {
            title: '批次标识',
            dataIndex: 'batchId',
            width: 180,
            ellipsis: true,
            render: (v) => highlightText(v, appliedBatchIdKeyword),
          },
          { title: '件数', dataIndex: 'pieceCount', width: 80, render: (v) => v ?? '-' },
          { title: '已生成单箱', dataIndex: 'materializedCount', width: 100, render: (v) => v ?? 0 },
          { title: '货物品名', dataIndex: 'cargoName', width: 140, ellipsis: true, render: (v) => v || '-' },
          { title: '供应商', dataIndex: 'fromSupplierId', width: 120, ellipsis: true, render: (id) => supplierLabel(id) },
          { title: '客户', dataIndex: 'belongClientId', width: 120, ellipsis: true, render: (id) => customerLabel(id) },
          { title: '运单号', dataIndex: 'shipmentId', width: 140, ellipsis: true, render: (v) => v || '-' },
          { title: '备注', dataIndex: 'batchRemark', width: 160, ellipsis: true, render: (v) => v || '-' },
          { title: '创建时间', dataIndex: 'createTimestamp', width: 160, render: (t) => (t ? new Date(t).toLocaleString() : '-') },
          {
            title: '操作',
            key: 'action',
            width: 80,
            fixed: 'right',
            render: (_, record) => (
              <Button
                type="link"
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  if (record.id) setBatchDrawerId(record.id);
                }}
              >
                详情
              </Button>
            ),
          },
        ]}
        pagination={serverTablePagination({
          page: batchPage,
          pageSize: batchPageSize,
          total: batchTotal,
          onPageChange: setBatchPage,
          onPageSizeChange: setBatchPageSize,
        })}
      />
    </>
  );

  const cartonTab = (
    <>
      <FilterDrawer variant="space" style={{ marginBottom: 16 }}>
        <Input
          placeholder="模糊搜索货物标识"
          allowClear
          style={{ width: 200 }}
          value={filterCartonIdInput}
          onChange={(e) => setFilterCartonIdInput(e.target.value)}
          onPressEnter={applyFilters}
        />
        <Input
          placeholder="模糊搜索运单号"
          allowClear
          style={{ width: 200 }}
          value={filterShipmentIdInput}
          onChange={(e) => setFilterShipmentIdInput(e.target.value)}
          onPressEnter={applyFilters}
        />
        <Button type="primary" onClick={applyFilters}>查询</Button>
        <Button onClick={resetFilters}>重置</Button>
        <Button onClick={() => openReceiveModal(selectedRowKeys as string[])} disabled={selectedRowKeys.length === 0}>
          收货
        </Button>
      </FilterDrawer>
      {selectedRowKeys.length > 0 && (
        <Space style={{ marginBottom: 12, display: 'flex' }}>
          <span style={{ color: '#666' }}>已选 <strong>{selectedRowKeys.length}</strong> 条</span>
          <Button size="small" onClick={() => setSelectedRowKeys([])}>清空选择</Button>
          <Button size="small" onClick={() => openReceiveModal(selectedRowKeys as string[])}>批量收货</Button>
          <Button size="small" onClick={() => openSupplierModal(selectedRowKeys as string[])}>批量改供应商</Button>
          <Tooltip title="敬请期待">
            <Button size="small" disabled>批量交代理仓</Button>
          </Tooltip>
        </Space>
      )}
      <AdaptiveTable
        loading={loading}
        dataSource={list}
        rowKey="id"
        scroll={{ x: 1000 }}
        rowSelection={{
          selectedRowKeys,
          onChange: (keys) => setSelectedRowKeys(keys),
        }}
        onRow={(record) => ({
          onClick: (e) => {
            if ((e.target as HTMLElement).closest('.ant-table-selection-column')) return;
            openDetail(record);
          },
          style: { cursor: 'pointer' },
        })}
        columns={[
          {
            title: '货物标识',
            dataIndex: 'cartonId',
            width: 160,
            ellipsis: true,
            render: (v) => highlightText(v, appliedCartonIdKeyword),
          },
          { title: '批次', dataIndex: 'batchId', width: 140, ellipsis: true, render: (v) => v || '-' },
          { title: '供应商', dataIndex: 'fromSupplierId', width: 120, ellipsis: true, render: (id) => supplierLabel(id) },
          { title: '客户', dataIndex: 'belongClientId', width: 120, ellipsis: true, render: (id) => customerLabel(id) },
          { title: '计费重量', dataIndex: 'billingWeightKg', width: 100, render: (v: number | undefined) => (v != null ? `${v} kg` : '-') },
          { title: '状态', dataIndex: 'state', width: 120, render: (s) => renderState(s || '') },
          {
            title: '运单号',
            dataIndex: 'shipmentId',
            width: 140,
            ellipsis: true,
            render: (v) => highlightText(v, appliedShipmentIdKeyword),
          },
          { title: '创建时间', dataIndex: 'createTimestamp', width: 160, render: (t) => (t ? new Date(t).toLocaleString() : '-') },
          { title: '操作', key: 'action', width: 140, fixed: 'right', render: (_, record) => (
            <Space onClick={(e) => e.stopPropagation()}>
              <Button type="link" size="small" onClick={() => openDetail(record)}>详情</Button>
              {record.state === 'preparing' && record.id && (
                <Button type="link" size="small" onClick={(e) => { e.stopPropagation(); openReceiveModal([record.id!]); }}>收货</Button>
              )}
              <Tooltip title="敬请期待"><Button type="link" size="small" disabled>交代理仓</Button></Tooltip>
            </Space>
          ) },
        ]}
        pagination={serverTablePagination({
          page,
          pageSize,
          total,
          onPageChange: setPage,
          onPageSizeChange: setPageSize,
        })}
      />
    </>
  );

  return (
    <>
      <Card
        title="货物列表"
        extra={
          <Space>
            <Button type="primary" onClick={() => setAddModalOpen(true)}>
              录入货物
            </Button>
            <Tooltip title="敬请期待">
              <Button disabled>交代理仓</Button>
            </Tooltip>
          </Space>
        }
      >
        <Tabs
          activeKey={activeTab}
          onChange={(k) => setActiveTab(k as TabKey)}
          items={[
            { key: 'batches', label: '货物批次', children: batchTab },
            { key: 'cartons', label: '单箱', children: cartonTab },
          ]}
        />
      </Card>

      <CartonBatchFormModal
        open={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        onCreated={onBatchCreated}
      />

      <CartonBatchDetailDrawer
        open={!!batchDrawerId}
        batchDbId={batchDrawerId}
        suppliers={suppliers}
        customers={customers}
        onClose={() => setBatchDrawerId(undefined)}
        onChanged={() => loadBatches()}
      />

      <Modal
        title={receiveTargetIds.length > 1 ? `批量收货（${receiveTargetIds.length} 箱）` : '收货'}
        open={receiveModalOpen}
        onCancel={closeReceiveModal}
        footer={[
          <Button key="cancel" onClick={closeReceiveModal}>取消</Button>,
          <Button key="submit" type="primary" loading={receiveSubmitting} onClick={submitReceive}>确认收货</Button>,
        ]}
        width={560}
        destroyOnClose
      >
        <Form form={receiveForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="arrivedBy" label="到达方式" rules={[{ required: true, message: '请选择到达方式' }]}>
            <Select
              placeholder="请选择"
              options={[
                { value: 0, label: '货拉拉' },
                { value: 1, label: '快递' },
              ]}
            />
          </Form.Item>
          <Form.Item name="queryParamOfShippment" label="查询单号">
            <Input placeholder="货拉拉或快递单号" />
          </Form.Item>
          <Form.Item label="照片">
            {receivePictures.map((p, idx) => (
              <div key={idx} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 8 }}>
                <Input
                  placeholder="图片标注，如货拉拉收货照片"
                  value={p.desc}
                  onChange={(e) => setReceivePictureDesc(idx, e.target.value)}
                  style={{ width: 180 }}
                />
                <Upload
                  accept="image/*"
                  showUploadList={false}
                  beforeUpload={(file) => {
                    const reader = new FileReader();
                    reader.onload = () => {
                      const data = reader.result as string;
                      setReceivePictureBase64(idx, data);
                    };
                    reader.readAsDataURL(file);
                    return false;
                  }}
                >
                  <Button size="small">{p.base64 ? '更换图片' : '选择图片'}</Button>
                </Upload>
                {p.base64 && <span style={{ color: '#52c41a', fontSize: 12 }}>已选</span>}
                <Button type="link" danger size="small" onClick={() => removeReceivePicture(idx)}>删除</Button>
              </div>
            ))}
            <Button type="dashed" size="small" onClick={addReceivePicture}>+ 添加照片</Button>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={`批量改供应商（${supplierTargetIds.length} 箱）`}
        open={supplierModalOpen}
        onCancel={closeSupplierModal}
        footer={[
          <Button key="cancel" onClick={closeSupplierModal}>取消</Button>,
          <Button key="submit" type="primary" loading={supplierSubmitting} onClick={submitBatchUpdateSupplier}>确认更新</Button>,
        ]}
        width={480}
        destroyOnClose
      >
        <Form form={supplierForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="fromSupplierId" label="供应商" rules={[{ required: true, message: '请选择供应商' }]}>
            <Select placeholder="选择供应商" showSearch optionFilterProp="label" options={suppliers} style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>

      <CartonDetailDrawer
        open={detailDrawerOpen}
        cartonId={detailCartonId}
        onClose={closeDetail}
        onSaved={() => load()}
      />
    </>
  );
};

export default CartonList;
